import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Trash2, Save, Search, Bookmark } from 'lucide-react';
import type { SavedSearch } from '@/hooks/useSavedSearches';

interface SavedSearchesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Query to save when the user confirms the dialog. */
  currentQuery: string;
  /** Controlled list — owned by useSavedSearches in GlobalSearch. */
  saved: SavedSearch[];
  onSave: (name: string, query: string) => boolean;
  onRemove: (id: string) => void;
  /** Executes a saved query in the global search palette. */
  onExecute: (query: string) => void;
}

export function SavedSearchesDialog({
  open,
  onOpenChange,
  currentQuery,
  saved,
  onSave,
  onRemove,
  onExecute,
}: SavedSearchesDialogProps) {
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Pre-fill the name with the current query each time the dialog opens.
  useEffect(() => {
    if (open) {
      setNewName(currentQuery.trim());
      setError(null);
    }
  }, [open, currentQuery]);

  const saveCurrent = () => {
    const ok = onSave(newName, currentQuery);
    if (!ok) {
      setError(
        currentQuery.trim()
          ? 'Não foi possível salvar (limite de 20 buscas salvas atingido?).'
          : 'Execute uma busca antes de salvar.',
      );
      return;
    }
    setNewName('');
    setError(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Buscas Salvas</DialogTitle>
        </DialogHeader>

        <div className="mb-4 flex gap-2">
          <Input
            placeholder="Nome da busca..."
            value={newName}
            onChange={(e) => {
              setNewName(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                saveCurrent();
              }
            }}
          />
          <Button onClick={saveCurrent} disabled={!newName.trim() || !currentQuery.trim()}>
            <Save className="mr-2 h-4 w-4" /> Salvar
          </Button>
        </div>
        {error && <p className="mb-2 text-xs text-red-500">{error}</p>}
        {!currentQuery.trim() && saved.length === 0 && (
          <p className="mb-2 flex items-center gap-1.5 text-xs text-slate-400">
            <Bookmark className="h-3.5 w-3.5" />
            Digite uma busca no comando de busca (⌘K) para salvá-la aqui.
          </p>
        )}

        <div className="max-h-[300px] space-y-2 overflow-y-auto">
          {saved.length === 0 && (
            <p className="py-4 text-center text-sm text-slate-400">Nenhuma busca salva.</p>
          )}
          {saved.map((s) => (
            <div key={s.id} className="group flex items-center justify-between rounded-md border bg-slate-50 p-2">
              <div className="flex min-w-0 flex-col overflow-hidden">
                <span className="truncate text-sm font-medium">{s.name}</span>
                <span className="truncate text-xs text-slate-500">{s.query}</span>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  title="Executar busca"
                  aria-label={`Executar busca salva ${s.name}`}
                  onClick={() => {
                    onExecute(s.query);
                    onOpenChange(false);
                  }}
                >
                  <Search className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-500 hover:text-red-600"
                  title="Remover busca salva"
                  aria-label={`Remover busca salva ${s.name}`}
                  onClick={() => onRemove(s.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
